import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import useUpdate from "../../../../../api/useUpdate";
import { useForm } from "react-hook-form";

const getDriverIdFromAmbulance = (assignedDriver) => {
  if (!assignedDriver) return "";
  if (typeof assignedDriver === "object") {
    return String(assignedDriver.id || assignedDriver.value || "");
  }
  return String(assignedDriver);
};

const AmbulanceDriverPaymentForm = ({ payment = null, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const isEdit = !!payment;
  const [drivers, setDrivers] = useState([]);
  const [ambulances, setAmbulances] = useState([]);

  // Fetch reference data
  const {
    data: driversData,
    isLoading: driversLoading,
  } = useFetchObjects(["drivers"], "drivers/", token);
  const {
    data: ambulancesData,
    isLoading: ambulancesLoading,
  } = useFetchObjects(["ambulances"], "ambulances/", token);
  useEffect(() => {
    if (driversData) setDrivers(driversData.results || driversData);
    if (ambulancesData) setAmbulances(ambulancesData.results || ambulancesData);
  }, [driversData, ambulancesData]);

  const {
    handleAdd,
    loading,
    resetForm,
  } = useAdd("ambulance-driver-payments", token);
  const {
    handleUpdate,
    loading: updateLoading,
    success: updateSuccess,
  } = useUpdate("ambulance-driver-payments", token, null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      payment_date: new Date().toISOString().slice(0, 10),
      driver: "",
      driver_phone: "",
      ambulance: "",
      ambulance_code: "",
      plate_number: "",
      trip_reference: "",
      payment_type: "Trip Allowance",
      amount: "0",
      currency: "USD",
      payment_method: "1",
      description: "",
    },
  });

  const driver = watch("driver");
  const ambulance = watch("ambulance");

  // Update driver phone when driver changes
  useEffect(() => {
    const selectedDriver = drivers.find((d) => String(d.id) === String(driver));
    setValue("driver_phone", selectedDriver?.phone || selectedDriver?.phone_number || "");
  }, [driver, drivers]);

  // Auto-fill the linked driver and ambulance details when ambulance changes
  useEffect(() => {
    const selectedAmbulance = ambulances.find((a) => String(a.id) === String(ambulance));
    setValue("driver", getDriverIdFromAmbulance(selectedAmbulance?.assigned_driver));
    setValue("ambulance_code", selectedAmbulance?.ambulance_code || "");
    setValue("plate_number", selectedAmbulance?.plate_number || "");
    setValue(
      "driver_phone",
      selectedAmbulance?.driver_phone || ""
    );
  }, [ambulance, ambulances]);

  useEffect(() => {
    if (isEdit && updateSuccess) {
      onSuccess?.();
    }
  }, [isEdit, updateSuccess, onSuccess]);

  useEffect(() => {
    if (!isEdit && resetForm) {
      navigate(-1);
    }
  }, [isEdit, resetForm, navigate]);

  useEffect(() => {
    if (!payment) {
      reset({
        payment_date: new Date().toISOString().slice(0, 10),
        driver: "",
        driver_phone: "",
        ambulance: "",
        ambulance_code: "",
        plate_number: "",
        trip_reference: "",
        payment_type: "Trip Allowance",
        amount: "0",
        currency: "USD",
        payment_method: "1",
        description: "",
      });
      return;
    }

    reset({
      payment_date: payment.payment_date || new Date().toISOString().slice(0, 10),
      driver: payment.driver?.id ? String(payment.driver.id) : String(payment.driver || ""),
      driver_phone: payment.driver_phone || "",
      ambulance: payment.ambulance?.id ? String(payment.ambulance.id) : String(payment.ambulance || ""),
      ambulance_code: payment.ambulance_code || "",
      plate_number: payment.plate_number || "",
      trip_reference: payment.trip_reference || "",
      payment_type: payment.payment_type || "Trip Allowance",
      amount: payment.amount?.toString?.() ?? String(payment.amount || "0"),
      currency: payment.currency || "USD",
      payment_method: String(payment.payment_method ?? "1"),
      description: payment.description || "",
    });
  }, [payment, reset]);

  const onSubmit = (data) => {
    data.payment_type = "Trip Allowance";
    if (isEdit) {
      handleUpdate(payment.id, data);
      return;
    }
    handleAdd(data);
  };

  const hiddenField = (name) => <input type="hidden" {...register(name)} />;

  if (driversLoading || ambulancesLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">{t("common.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14, 116, 144, 0.12), transparent 28%), linear-gradient(180deg, #f5fbfd 0%, #ffffff 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: 4,
            color: "white",
            background: "linear-gradient(135deg, #0f5c73 0%, #117a93 55%, #0b6278 100%)",
            boxShadow: "0 24px 60px rgba(15, 92, 115, 0.22)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2 }}>
                {t("finance.ambulanceBilling", { defaultValue: "Ambulance Billing" })}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {isEdit
                  ? t("finance.editAmbulanceDriverPayment", { defaultValue: "Edit Ambulance Driver Payment" })
                  : t("finance.ambulanceDriverPaymentTitle", {
                      defaultValue: "Ambulance Driver Payment",
                    })}
              </Typography>
              <Typography sx={{ mt: 0.75, opacity: 0.9, maxWidth: 760 }}>
                {t("finance.driverPaymentHeroDesc", {
                  defaultValue: "Log driver allowances and trip payments with auto-filled ambulance information.",
                })}
              </Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                label={t("finance.ambulanceDriverPaymentTab", { defaultValue: "Driver Payment" })}
                sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "white" }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
              {isEdit
                ? t("finance.editTripPaymentDetails", {
                    defaultValue: "Edit Trip Payment Details",
                  })
                : t("finance.tripPaymentDetails", {
                    defaultValue: "Trip Payment Details",
                  })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {t("finance.tripPaymentDetailsDesc", {
                defaultValue:
                  "Choose the driver and ambulance first. The code and plate will populate automatically.",
              })}
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {hiddenField("driver_phone")}
              {hiddenField("ambulance_code")}
              {hiddenField("plate_number")}
              {hiddenField("payment_type")}
              {hiddenField("currency")}
              {hiddenField("payment_method")}
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.date", { defaultValue: "Date" })}
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...register("payment_date")}
                    error={!!errors.payment_date}
                    helperText={errors.payment_date ? errors.payment_date.message : ""}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("finance.driver", { defaultValue: "Driver" })}
                    {...register("driver")}
                    defaultValue=""
                    error={!!errors.driver}
                    helperText={
                      errors.driver
                        ? errors.driver.message
                        : t("finance.autoSelectedFromAmbulance", {
                            defaultValue: "Auto-selected from the ambulance",
                          })
                    }
                  >
                    <MenuItem value="">-- {t("common.select", { defaultValue: "Select" })} --</MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.name || `${driver.firstname || ""} ${driver.lastname || ""}`.trim() || `Driver ${driver.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.tripReference", { defaultValue: "Trip Reference" })}
                    fullWidth
                    {...register("trip_reference")}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("finance.ambulance", { defaultValue: "Ambulance" })}
                    {...register("ambulance")}
                    defaultValue=""
                    error={!!errors.ambulance}
                    helperText={
                      errors.ambulance
                        ? errors.ambulance.message
                        : t("finance.pickAmbulanceTiedPayment", {
                            defaultValue: "Pick the ambulance tied to this payment",
                          })
                    }
                  >
                    <MenuItem value="">-- {t("common.select", { defaultValue: "Select" })} --</MenuItem>
                    {ambulances.map((ambulance) => (
                      <MenuItem key={ambulance.id} value={ambulance.id}>
                        {ambulance.ambulance_code} - {ambulance.plate_number}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("finance.amount", { defaultValue: "Amount" })}
                    type="number"
                    fullWidth
                    inputMode="decimal"
                    {...register("amount", {
                      required: true,
                      validate: (value) => parseFloat(value) >= 0 || "Must be a positive number",
                    })}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.amount}
                    helperText={errors.amount ? errors.amount.message : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("finance.description", { defaultValue: "Description" })}
                    {...register("description")}
                    fullWidth
                    multiline
                    minRows={4}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={isEdit ? onSuccess : () => navigate(-1)}
                      sx={{ textTransform: "none", fontWeight: 700, px: 4, py: 1.4 }}
                    >
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={loading || updateLoading}
                      sx={{ textTransform: "none", fontWeight: 800, px: 4, py: 1.4 }}
                    >
                      {isEdit ? t("common.update", { defaultValue: "Update" }) : t("common.save", { defaultValue: "Save" })}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AmbulanceDriverPaymentForm;
